import { menus } from "@/constants/menu";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { background, black, text } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { TMenu } from "@/types/menu";
import { hexToRgba } from "@/utils/pieces";
import { Divider, Drawer, Stack, Tooltip, Typography, styled } from "@mui/material";
import Box from "@mui/material/Box";
import zIndex from "@mui/material/styles/zIndex";
import { FC, useMemo } from "react";
import { matchRoutes, useLocation, useNavigate } from "react-router-dom";

type TAppDrawerProps = { isOpenDrawer: boolean; toggleDrawer: () => void };

export const AppDrawer: FC<TAppDrawerProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();

  const DrawerContent = useMemo(() => {
    // Filter menus based on visible property (default: true)
    const visibleMenus = menus.filter((item) => item.visible !== false);

    return (
      <ListWrapper isLaptop={isLaptop} gap={1}>
        <Stack gap={1.5}>
          {visibleMenus.map((item, index) => {
            return (
              <Stack gap={2} key={index}>
                <MenuItem key={item.text} {...item} />
                {index < visibleMenus.length - 1 && <Divider color={background.white} sx={{ opacity: 0.3 }} />}
              </Stack>
            );
          })}
        </Stack>
      </ListWrapper>
    );
  }, [pathname, isLaptop]);

  if (!isLaptop) return DrawerContent;
  return (
    <Drawer
      open={isOpenDrawer}
      onClose={toggleDrawer}
      hideBackdrop
      ModalProps={{ sx: { width: "fit-content" } }}
      PaperProps={{ sx: { bgcolor: background.main, height: "calc(100vh - 57px)", top: "64px" } }}
    >
      {DrawerContent}
    </Drawer>
  );
};

const MenuItem: FC<TMenu> = (props) => {
  const { isLaptop } = useMediaQuery();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isActive = props.relatedRoutes?.some((route) => Boolean(matchRoutes([{ path: route }], pathname)));

  const childrenContent = useMemo(() => {
    // Filter children based on visible property (default: true)
    const visibleChildren = props.children?.filter((item) => item.visible !== false);
    return visibleChildren?.map((item) => <MenuItem key={item.text} {...item} />);
  }, [props.children]);

  const content = useMemo(() => {
    const Icon = props.icon;

    return (
      <Stack>
        <MenuItemContent
          p={isLaptop && !Icon ? 0 : 1}
          pl={isLaptop ? 1 : props.path ? 2 : 1}
          direction="row"
          alignItems="center"
          gap={1.5}
          justifyContent={isLaptop ? "center" : "inherit"}
        >
          {Icon && (
            <IconWrapper>
              <Icon fontSize={24} color={text.white} />
            </IconWrapper>
          )}

          {!isLaptop && (
            <Typography variant={props.path ? "body14Bold" : "cap12Bold"} color={props.path ? text.white : text.tertiary}>
              {props.text}
            </Typography>
          )}
        </MenuItemContent>
        <Stack gap={1}>{childrenContent}</Stack>
      </Stack>
    );
  }, [props.text, props.icon, props.path, isLaptop, navigate]);

  if (!props.path) return content;
  if (isLaptop)
    return (
      <Tooltip title={props.text} placement="right">
        <ListItemWrapper
          isLaptop={isLaptop}
          className={isActive ? "active" : ""}
          onClick={() => {
            if (!props.path) return;
            navigate(props.path);
          }}
        >
          {content}
        </ListItemWrapper>
      </Tooltip>
    );
  return (
    <ListItemWrapper
      isLaptop={isLaptop}
      className={isActive ? "active" : ""}
      onClick={() => {
        if (!props.path) return;
        navigate(props.path);
      }}
    >
      {content}
    </ListItemWrapper>
  );
};

const ListWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  padding: ${spacing.md} ${spacing.sm};
  width: ${({ isLaptop }) => (isLaptop ? "80px" : "auto")};
  min-width: ${({ isLaptop }) => (isLaptop ? "auto" : "242px")};
  background-color: ${black[30]};
  color: ${text.white};
  min-height: calc(100vh - 56px);
  height: 100%;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 56px;
  z-index: ${zIndex.drawer};
`;

const ListItemWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLaptop",
})<{ isLaptop: boolean }>`
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border-radius: ${rounded.sm};
  &.active {
    background-color: ${hexToRgba(background.white, 0.3)} !important;
  }

  &:hover {
    background-color: ${hexToRgba(background.white, 0.1)};
  }
`;

const MenuItemContent = styled(Stack)`
  position: relative;
`;

const IconWrapper = styled(Box)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
